import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_URL } from '../../utils/api';
import { cxGet, authHeaders, useCxEntity, Panel } from './cxShared';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { DownloadSimple, MicrosoftExcelLogo, Phone } from '@phosphor-icons/react';
import { toast } from 'sonner';

const XLSX_MIME = 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';

const isoDaysAgo = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
};
const todayIso = () => new Date().toISOString().slice(0, 10);

export default function CxReports() {
  const { entityId } = useCxEntity();
  const scopeParam = entityId && entityId !== 'all' ? `entity_id=${entityId}` : '';

  const [campuses, setCampuses] = useState([]);
  const [categories, setCategories] = useState([]);

  useEffect(() => {
    const eq = scopeParam ? `?${scopeParam}` : '';
    cxGet(`/campuses${eq}`).then((r) => setCampuses(Array.isArray(r) ? r : [])).catch(() => setCampuses([]));
    cxGet('/subscriber-categories').then((r) => setCategories(Array.isArray(r) ? r : [])).catch(() => setCategories([]));
  }, [scopeParam]);

  const download = async (url, filename, mime) => {
    try {
      const res = await axios.get(url, { headers: authHeaders(), responseType: 'blob' });
      const blob = new Blob([res.data], { type: mime });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(link.href);
      toast.success(`${filename} downloaded`);
    } catch (e) {
      toast.error('Download failed');
    }
  };

  const withScope = (base) => (scopeParam ? `${base}${base.includes('?') ? '&' : '?'}${scopeParam}` : base);

  const [summaryRange, setSummaryRange] = useState({ start: isoDaysAgo(30), end: todayIso() });
  const [listRange, setListRange] = useState({ start: isoDaysAgo(30), end: todayIso() });
  const [exportFilters, setExportFilters] = useState({ performance: '', campus: '', category: '' });
  const [wbBucket, setWbBucket] = useState('at_risk');

  return (
    <div className="space-y-6" data-testid="cx-reports-page">
      <Panel title="Customer Experience Reports" testid="cx-reports-section">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-start">
          <ReportCard
            icon={<Phone size={40} weight="duotone" className="text-[#0A2540]" />}
            title="Win-back Worklist"
            blurb="Customers who have stopped refilling, ready for outreach — name, mobile, campus, last refill, days idle, tier and wallet balance."
            testid="folder-winback"
            downloadTestid="dl-winback"
            onDownload={() => download(
              withScope(`${API_URL}/api/cx/worklist/export?bucket=${wbBucket}`),
              `winback-${wbBucket}_${todayIso()}.xlsx`,
              XLSX_MIME,
            )}
          >
            <div>
              <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">Spectrum bucket</Label>
              <Select value={wbBucket} onValueChange={setWbBucket}>
                <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid="winback-bucket"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="at_risk">At Risk (15–29d)</SelectItem>
                  <SelectItem value="lost">Lost (30d+)</SelectItem>
                  <SelectItem value="at_risk,lost">At Risk + Lost</SelectItem>
                  <SelectItem value="cooling">Cooling (8–14d)</SelectItem>
                  <SelectItem value="active">Active (0–7d)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </ReportCard>

          <ReportCard
            icon={<MicrosoftExcelLogo size={40} weight="duotone" className="text-[#1D6F42]" />}
            title="Registration Summary"
            blurb="New registrations grouped by category, campus and gender for the selected period."
            testid="folder-registration-summary"
            downloadTestid="dl-registration-summary"
            onDownload={() => download(
              withScope(`${API_URL}/api/reports/subscribers/registration-summary?start_date=${summaryRange.start}&end_date=${summaryRange.end}`),
              `registration-summary_${summaryRange.start}_${summaryRange.end}.xlsx`,
              XLSX_MIME,
            )}
          >
            <DateRange value={summaryRange} onChange={setSummaryRange} />
          </ReportCard>

          <ReportCard
            icon={<MicrosoftExcelLogo size={40} weight="duotone" className="text-[#1D6F42]" />}
            title="Registration List"
            blurb="One row per subscriber registered in the selected period."
            testid="folder-registration-list"
            downloadTestid="dl-registration-list"
            onDownload={() => download(
              withScope(`${API_URL}/api/reports/subscribers/registration-list?start_date=${listRange.start}&end_date=${listRange.end}`),
              `registrations_${listRange.start}_${listRange.end}.xlsx`,
              XLSX_MIME,
            )}
          >
            <DateRange value={listRange} onChange={setListRange} />
          </ReportCard>

          <ReportCard
            icon={<MicrosoftExcelLogo size={40} weight="duotone" className="text-[#1D6F42]" />}
            title="Subscribers List"
            blurb="Full subscriber book with lifetime refill / top-up metrics, filtered by performance, campus or category."
            testid="folder-subscribers-export"
            downloadTestid="dl-subscribers-export"
            onDownload={() => {
              const qs = new URLSearchParams();
              if (exportFilters.performance) qs.set('performance', exportFilters.performance);
              if (exportFilters.campus) qs.set('campus', exportFilters.campus);
              if (exportFilters.category) qs.set('category', exportFilters.category);
              if (scopeParam) qs.set('entity_id', entityId);
              const url = `${API_URL}/api/reports/subscribers/export${qs.toString() ? `?${qs}` : ''}`;
              download(url, `subscribers_${todayIso()}.xlsx`, XLSX_MIME);
            }}
          >
            <div className="grid grid-cols-1 gap-2">
              <FilterInput
                label="Performance"
                value={exportFilters.performance}
                onChange={(v) => setExportFilters((p) => ({ ...p, performance: v }))}
                testid="export-performance"
                placeholder="All performance"
                options={[
                  { v: 'excellent', l: 'Excellent' },
                  { v: 'good', l: 'Good' },
                  { v: 'normal', l: 'Normal' },
                  { v: 'dormant', l: 'Dormant' },
                ]}
              />
              <FilterInput
                label="Campus"
                value={exportFilters.campus}
                onChange={(v) => setExportFilters((p) => ({ ...p, campus: v }))}
                testid="export-campus"
                placeholder="All campuses"
                options={campuses.map((c) => ({ v: c.campus_name, l: c.campus_name }))}
              />
              <FilterInput
                label="Category"
                value={exportFilters.category}
                onChange={(v) => setExportFilters((p) => ({ ...p, category: v }))}
                testid="export-category"
                placeholder="All categories"
                options={categories.map((k) => ({ v: k.category, l: k.category }))}
              />
            </div>
          </ReportCard>

          <ReportCard
            icon={<MicrosoftExcelLogo size={40} weight="duotone" className="text-[#1D6F42]" />}
            title="Subscribers Summary"
            blurb="One-click snapshot of the full book: by category, campus, gender and performance."
            testid="folder-subscribers-summary"
            downloadTestid="dl-subscribers-summary"
            onDownload={() => download(
              withScope(`${API_URL}/api/reports/subscribers/summary`),
              `subscribers-summary_${todayIso()}.xlsx`,
              XLSX_MIME,
            )}
          />
        </div>
      </Panel>
    </div>
  );
}

function ReportCard({ icon, title, blurb, children, onDownload, testid, downloadTestid }) {
  return (
    <div
      data-testid={testid}
      className="flex flex-col gap-3 p-5 rounded-2xl border border-[#EDE6D8] bg-white h-full"
    >
      <div className="flex items-start gap-3">
        <span className="shrink-0">{icon}</span>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-[#1C1917] leading-tight" style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}>{title}</p>
          <p className="text-[11px] text-[#78716C] mt-1 leading-snug">{blurb}</p>
        </div>
      </div>

      {children && (
        <div className="space-y-3 border-t border-[#EDE6D8] pt-3">
          {children}
        </div>
      )}

      <Button
        onClick={onDownload}
        data-testid={downloadTestid}
        className="mt-auto w-full bg-[#0A2540] hover:bg-[#0A2540]/90 text-white rounded-lg h-9"
      >
        <DownloadSimple size={15} className="mr-1.5" /> Download Excel
      </Button>
    </div>
  );
}

function DateRange({ value, onChange }) {
  return (
    <div className="grid grid-cols-2 gap-2">
      <div>
        <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">From</Label>
        <Input type="date" value={value.start} onChange={(e) => onChange({ ...value, start: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid="date-start" />
      </div>
      <div>
        <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">To</Label>
        <Input type="date" value={value.end} onChange={(e) => onChange({ ...value, end: e.target.value })} className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid="date-end" />
      </div>
    </div>
  );
}

function FilterInput({ label, value, onChange, options, placeholder, testid }) {
  return (
    <div>
      {label && <Label className="text-[10px] text-[#78716C] uppercase tracking-wider">{label}</Label>}
      <Select value={value || '__all__'} onValueChange={(v) => onChange(v === '__all__' ? '' : v)}>
        <SelectTrigger className="border-[#EDE6D8] rounded-lg h-9 mt-1" data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">{placeholder}</SelectItem>
          {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}
